// Bulk import operations for Firestore
// This module handles bulk data operations and imports

import {
  collection,
  doc,
  addDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../utils/firebase";
import type {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  MusicIndustry,
} from '../types';
import { OutreachCategory } from '../../../types/outreach';
import { OUTREACH_DOC_PATH } from '../utils/constants';
import { deleteAllOutreachItems } from './crud';

/**
 * Import data from prepared data arrays to Firebase
 *
 * @param reactionChannels Array of reaction channels to import
 * @param playlistingChannels Array of playlisting channels to import
 * @param spotifyPlaylists Array of Spotify playlists to import
 * @param musicBlogs Array of music blogs to import
 * @param musicIndustry Array of music industry contacts to import
 */
export const importOutreachData = async (
  reactionChannels: Omit<ReactionChannel, "id">[],
  playlistingChannels: Omit<PlaylistingChannel, "id">[],
  spotifyPlaylists: Omit<SpotifyPlaylist, "id">[],
  musicBlogs: Omit<MusicBlog, "id">[],
  musicIndustry: Omit<MusicIndustry, "id">[]
): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { created: new Date() }, { merge: true });

    // Delete all existing data before importing new data
    console.log("Deleting all existing data before import...");
    await deleteAllOutreachItems(OutreachCategory.REACTION_CHANNELS);
    await deleteAllOutreachItems(OutreachCategory.PLAYLISTING_CHANNELS);
    await deleteAllOutreachItems(OutreachCategory.SPOTIFY_PLAYLISTS);
    await deleteAllOutreachItems(OutreachCategory.MUSIC_BLOGS);
    await deleteAllOutreachItems(OutreachCategory.MUSIC_INDUSTRY);
    console.log("All existing data deleted successfully");

    // Create collections if they don't exist and add data
    const addItems = async <T>(
      category: OutreachCategory,
      items: Omit<T, "id">[]
    ) => {
      try {
        console.log(`Adding ${items.length} items to ${category}...`);
        const collectionRef = collection(
          db,
          `${OUTREACH_DOC_PATH}/${category}`
        );
        for (const item of items) {
          await addDoc(collectionRef, item);
        }
        console.log(`Successfully added ${items.length} items to ${category}`);
      } catch (error) {
        console.error(`Error adding items to ${category}:`, error);
        throw error;
      }
    };

    // Add all items to their respective collections
    await addItems<ReactionChannel>(
      OutreachCategory.REACTION_CHANNELS,
      reactionChannels
    );
    await addItems<PlaylistingChannel>(
      OutreachCategory.PLAYLISTING_CHANNELS,
      playlistingChannels
    );
    await addItems<SpotifyPlaylist>(
      OutreachCategory.SPOTIFY_PLAYLISTS,
      spotifyPlaylists
    );
    await addItems<MusicBlog>(OutreachCategory.MUSIC_BLOGS, musicBlogs);
    await addItems<MusicIndustry>(
      OutreachCategory.MUSIC_INDUSTRY,
      musicIndustry
    );

    console.log("Successfully imported all data to Firebase");
  } catch (error) {
    console.error("Error importing data to Firebase:", error);
    throw error;
  }
};